import { Component, OnInit, Input, Output, SimpleChanges, EventEmitter, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import * as words from '../../../../services/language';
import { formatCurrency } from 'src/app/utils/format';
import { ago } from 'src/app/utils/date';
import { clean } from 'src/app/utils/numeric';
import { PAYMENT_METHODS, PAYMENT_SOURCES_BY_METHOD } from 'src/app/constants/payments';
import { MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MILLIS } from 'src/app/constants/list';
import moment from 'moment';
import { Subject, Subscription } from "rxjs";
import { Address } from 'src/app/models/address.model';
import { PayLine } from 'src/app/models/pay-line.model';

@Component({
    selector: 'take-payment',
    templateUrl: './take-payment.component.html',
    styleUrls: ['./take-payment.component.scss'],
    standalone: false
})
export class TakePaymentComponent implements OnInit, OnDestroy {

    // Unique internal identity of the contact whose soon due payments will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Available payments for the given contact.
    @Input() pays: Object | null = null

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // The payment allocation order from the lot setup. 
    // A list of payment category strings.
    @Input() allocate: Array<string> | null = null

    // A payment was taken so lets update our available payments and any suppporting displays.
    @Input() payThisAmount: Subject<number> | null = null

    // Communicate the processing of a payment to those who will listen.
    @Output() paymentTaken: EventEmitter<Object> = new EventEmitter()

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Stored payment accounts for the current contact.
    protected accounts: Array<any> = []

    // Top level payment form with basic details.
    protected form: FormGroup;

    // Payment account input form when a saved account is not used.
    protected account: FormGroup;

    // A list of available payment methods in the sysem
    protected methods = PAYMENT_METHODS;

    // A list of potential payment sources currently accepted by the system.
    protected sources = PAYMENT_SOURCES_BY_METHOD;

    // A list of zip codes matching the inquiry.
    protected zipCodeList: Address[] = []

    // Latest zip code search inquiry
    protected zipInquiry = null

    // The currenly selected terms identity (for late fees modal)
    protected selectedTermsId = null

    // The zip code serach timeout identity (debounce)
    private timeOutID: ReturnType<typeof setTimeout> | null = null

    private subscriptions: Subscription[] = []

    /**
     * Helper method to consistently get the list of form payments as an Array.
     */
    get payments() {
        return this.form.controls['payments'] as FormArray;
    }

    /**
     * Get the total amount the contact wants to pay today.
     */
    get amountToPay() { return clean(this.form.get('amount').value) }

    /**
     * A list of years available for card expiry date.
     */
    get years() {
        const current = (new Date()).getFullYear()
        
        const items = []
        for (let index=0; index <= 10; index++) {
            items.push(current + index)
        }

        return items
    }

    /**
     * Grab the current payment method type
     */
    get payMethodType(): string { 
        if (!this.account || !this.account.get('type') || !this.account.get('type').value) {
            return ''
        }

        return this.account.get('type').value
    }

    /**
     * Grab the selected stored account type
     */
    get storedAcctMethodType(): string { 
        if (!this.accounts || (this.accounts.length <= 0)) {
            return ''
        }

        const acctIdSelected = parseInt(this.form.get('accountId').value)
        if (!acctIdSelected || (acctIdSelected <= 0)) {
            return ''
        }

        const acctSelected = this.accounts.find((item) => item.id == acctIdSelected)
        if (!acctSelected || !acctSelected.type) {
            return ''
        }

        return acctSelected.type.toLowerCase()
    }

    /**
     * Is the new payment method cash?
     */
    get cash(): boolean { 
        if (!this.payMethodType) {
            return false
        }

        return (this.payMethodType === 'cash')
    }

    /**
     * Is the new payment method ach checking or savings?
     */
    get bank(): boolean { 
        if (!this.payMethodType) {
            return false
        }

        return (['ach checking', 'ach savings'].includes(this.payMethodType))
    }

    /**
     * Is the new payment method a credit or debit card?
     */
    get card(): boolean { 
        if (!this.payMethodType) {
            return false
        }

        return (['credit card', 'debit card', 'pre-paid debit card'].includes(this.payMethodType))
    }

    /**
     * Is the new payment method a check?
     */
    get check(): boolean { 
        if (!this.payMethodType) {
            return false
        }

        return (['check', 'travelers check', 'cashiers check', 'money order'].includes(this.payMethodType))
    }

    /**
     * Is the new payment method from a third-party?
     */
    get thirdParty(): boolean { 
        if (!this.payMethodType) {
            return false
        }

        return (this.payMethodType === 'third-party')
    }

    constructor (private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            paid: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            source: new FormControl<string>('walk-in', [Validators.required]), // so we can still have a source when a saved account is selected.
            amount: new FormControl<number>(0, []),
            accountId: new FormControl<number | null>(null, []),
            payments: this.fb.array([]) // saleId, due, category, amount of desired allocation
        })

        this.account = this.fb.group({
            type: new FormControl<string>('cash', [Validators.required]),
            source: new FormControl<string>('walk-in', [Validators.required]),
            holder: new FormControl<string>('', []),
            address: new FormControl<string>('', []),
            city: new FormControl<string>('', []),
            state: new FormControl<string>('', []),
            zipCode: new FormControl<string>('', []),
            route: new FormControl<string | null>(null, []),
            number: new FormControl<string>('', []),
            code: new FormControl<string | null>(null, []),
            expiry_month: new FormControl<number>(1, []),
            expiry_year: new FormControl<number>((new Date()).getFullYear(), []),
            expires: new FormControl<Date | null>(null, []),
            checkNumber: new FormControl<string | null>(null, []),
            paidIn100s: new FormControl<string>(formatCurrency(0), []),
            confirmation: new FormControl<string | null>(null, []),
            save: new FormControl<boolean>(false, [])
        })
    }

    ngOnInit() {
        this.applyValidators()
        this.fetchPaymentAccounts()

        this.subscriptions.push(
            this.payThisAmount.subscribe((toPay: number) => {
                if (!toPay || (toPay <= 0)) {
                    return
                }

                this.form.get('amount').setValue(formatCurrency(toPay))
                this.form.get('amount').markAsPristine()
                this.selectPays(undefined)
            })
        )

        this.subscriptions.push(
            this.account.get('type').valueChanges.subscribe((newType) => {
                if (!newType || !this.sources || !this.sources[newType]) {
                    return
                }

                this.account.get('source').setValue(this.sources[newType][0].toLowerCase())
                this.form.get('source').setValue(this.sources[newType][0].toLowerCase())
                this.applyValidators()
            })
        )

        this.subscriptions.push(
            this.form.get('accountId').valueChanges.subscribe((acctIdChosen) => {
                if (!acctIdChosen || !this.accounts || !this.sources) {
                    return
                }

                const chosenAcct = this.accounts.find((item) => item.id == acctIdChosen)
                if (!chosenAcct || !chosenAcct.type) {
                    return
                }

                this.form.get('source').setValue(this.sources[chosenAcct.type.toLowerCase()][0].toLowerCase())
                this.account.get('source').setValue(this.sources[chosenAcct.type.toLowerCase()][0].toLowerCase())
            })
        )

        this.subscriptions.push(
            this.account.get('zipCode').valueChanges.subscribe((zip) => {
                if (!this.account.get('zipCode')) {
                    return
                }

                const code = this.account.get('zipCode').value
                if (!code || (code.length < MIN_SEARCH_CHARS)) {
                    return
                }

                if (this.zipInquiry && (code === this.zipInquiry)) { // hasn't changed?
                    return
                }

                clearTimeout(this.timeOutID)

                this.timeOutID = setTimeout(() => {
                    this.zipCodeList = []
                    this.zipInquiry = code

                    this.master.get(`readLocationByZipcode?zipcode=${code}`, res => {
                        if (!res) {
                            this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                            return
                        }

                        if (res.status !== 200) {
                            return
                        }

                        this.zipCodeList = res.data.locations

                        // autocomplete
                        if ((code >= 5) && (this.zipCodeList.length === 1)) {
                            this.zipSelected(this.zipCodeList[0])
                        }
                    })
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.ms.channelComponents$.subscribe((event) => {
                if (!event || !event.message) {
                    return
                }

                switch (event.message) {
                    case 'contactRead':
                        const contact = event.value
                        if (!contact) {
                            break
                        }
                        
                        this.account.get('holder').setValue(contact.name, {emitEvent: false})
                        if (contact.isIndividual) {
                            this.account.get('holder').setValue(`${contact.firstName} ${contact.lastName || ''}`.trim(), {emitEvent: false})
                        }

                        this.determineAddress(contact.contactAddresses)
                        break
                }
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId) {
            this.fetchPaymentAccounts()
        }

        if (changes.pays) {
            this.identifyAvailablePayments()
        }
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }

        this.subscriptions = []

        if (this.timeOutID) {
            clearTimeout(this.timeOutID)
            this.timeOutID = null
        }
    }

    /**
     * After a payment is taken let's reset all of the forms.
     */
    private reset = (): void => {
        this.payments.clear()
        this.form.reset()
        this.account.reset()

        this.form.get('paid').setValue(moment().format('YYYY-MM-DD'))
        this.form.get('amount').setValue(formatCurrency(0))
        this.form.get('source').setValue(this.sources['cash'][0].toLowerCase())
        this.account.get('type').setValue('cash')
        this.account.get('source').setValue(this.sources['cash'][0].toLowerCase())
        this.account.get('expiry_month').setValue(1)
        this.account.get('expiry_year').setValue((new Date()).getFullYear())
    }

    /**
     * Add/remove validators based on the current payment method type.
     */
    private applyValidators = () => {
        this.form.get('paid').disable()

        if (!this.account || !this.account.get('type')) {
            return
        }

        const type = this.account.get('type').value
        if (!type || (type.length <= 0)) {
            return
        }

        this.account.get('checkNumber').clearValidators()
        this.account.get('expiry_month').clearValidators()
        this.account.get('expiry_year').clearValidators()
        this.account.get('number').clearValidators()
        this.account.get('code').clearValidators()
        this.account.get('route').clearValidators()
        this.account.get('confirmation').clearValidators()

        this.account.get('checkNumber').updateValueAndValidity()
        this.account.get('expiry_month').updateValueAndValidity()
        this.account.get('expiry_year').updateValueAndValidity()
        this.account.get('number').updateValueAndValidity()
        this.account.get('code').updateValueAndValidity()
        this.account.get('route').updateValueAndValidity()
        this.account.get('confirmation').updateValueAndValidity()

        switch (type) {
            case 'cash':
                break
            case 'check':
            case 'money order':
            case 'cashiers check':
            case 'travelers check':
                this.account.get('checkNumber').setValidators([Validators.pattern('^[0-9]*$'), Validators.required])
                this.account.get('checkNumber').updateValueAndValidity()
                break
            case 'credit card':
            case 'debit card':
                this.account.get('expiry_month').setValidators([Validators.pattern('^[0-9]{1,2}$'), Validators.required])
                this.account.get('expiry_year').setValidators([Validators.pattern('^[0-9]{4}$'), Validators.required])
                this.account.get('number').setValidators([Validators.pattern('^[0-9]{4,17}$'), Validators.required])
                this.account.get('code').setValidators([Validators.pattern('^[0-9]{3,4}$')])

                this.account.get('expiry_month').updateValueAndValidity()
                this.account.get('expiry_year').updateValueAndValidity()
                this.account.get('number').updateValueAndValidity()
                this.account.get('code').updateValueAndValidity()
                break
            case 'bank draft':
                this.account.get('number').setValidators([Validators.pattern('^[0-9]{4,17}$'), Validators.required])
                this.account.get('route').setValidators([Validators.pattern('^[0-9]{9}$'), Validators.required])

                this.account.get('number').updateValueAndValidity()
                this.account.get('route').updateValueAndValidity()
                break
            case 'third-party':
                this.account.get('confirmation').setValidators([Validators.required])
                this.account.get('confirmation').updateValueAndValidity()
                break
        }
    }

    /** 
     * Fetch the the stored payment accounts (tokens) for the curent contact.
    */
    private fetchPaymentAccounts = (): void => {
        if (this.loading) {
            return
        }

        if (!this.contactId || (this.contactId <= 0)) {
            this.reset()
            return
        }

        this.loading = true

        this.accounts.length = 0
        this.accounts = []

        this.master.get(`contacts/${this.contactId}/accounts`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.accounts = res.data
            if (!this.accounts || (this.accounts.length <= 0)) {
                return
            }

            this.form.get('accountId').setValue(this.accounts[0].id)
        })
    }

    /**
     * Determine what needs to be paid or what is due and
     * add them to the payments array in the form.
     */
    private identifyAvailablePayments = (): void => {
        this.payments.clear()

        if (!this.pays || (Object.keys(this.pays).length <= 0)) {
            return
        }
        
        const today = JSON.parse(JSON.stringify(this.pays['today']))
        if (today && (today.total > 0)) {
            this.form.get('amount').setValue(formatCurrency(today.total))
            this.account.get('paidIn100s').setValue(formatCurrency(today.total - (today.total % 100)))
            this.addPayments(today.items, true)
        }

        const next = JSON.parse(JSON.stringify(this.pays['next']))
        if (next && (next.total > 0)) {
            this.addPayments(next.items)
        }
    }

    /**
     * Add the given payments to the available payments.
     * @param pays A list of payments that are vailable to pay.
     * @param selected Indicates the payment will be selected, initially, for payment.
     */
    private addPayments = (pays: Array<Object>, selected: Boolean = false): void => {
        if (!pays || !Array.isArray(pays) || (pays.length <= 0)) {
            return
        }

        for (const pay of pays) {            
            let payment = this.payments.controls.find((item) => item.get('id').value == pay['id']) // pay['id'] = saleId

            if (!payment) {
                payment = this.fb.group({
                    id: new FormControl<number>(pay['id'], []),
                    termsId: new FormControl<number>(pay['termsId'], []),
                    label: new FormControl<string>(pay['vehicle'], []),
                    amount: new FormControl<string>(formatCurrency(0), []),
                    full: new FormControl<string>(formatCurrency(0), []),
                    url: new FormControl<number>(pay['url'], []),
                    selected: new FormControl<Boolean>(selected, []),
                    fees: new FormControl<Boolean>(false, []),
                    lines: this.fb.array([])
                })

                this.payments.push(payment)
            }

            const lines = (payment.get('lines') as FormArray)
            if (!lines) {
                continue
            }

            let line = lines.controls.find((item) => item.get('category').value == pay['category'])

            if (!line) {
                line = this.fb.group({
                    lienholder: new FormControl<string>(pay['lienholder'] || null, []),
                    category: new FormControl<string>(pay['category'], []),
                    due: new FormControl<string>(pay['due'], []),
                    amount: new FormControl<string>(formatCurrency(0), []),
                    full: new FormControl<string>(formatCurrency(0), []),
                    selected: new FormControl<Boolean>(selected, [])
                })

                if (pay['category'].toLowerCase() === 'late fee') {
                    payment.get('fees').setValue(true)
                }

                const amount = parseFloat(pay['amount'])
                if (amount && !isNaN(amount) && (amount > 0)) {
                    lines.push(line)
                }
            }

            payment.get('amount').setValue(formatCurrency(clean(payment.get('amount').value) + parseFloat(pay['amount'])))
            payment.get('full').setValue(formatCurrency(clean(payment.get('full').value) + parseFloat(pay['amount'])))

            line.get('amount').setValue(formatCurrency(clean(line.get('amount').value) + parseFloat(pay['amount'])))
            line.get('full').setValue(formatCurrency(clean(line.get('full').value) + parseFloat(pay['amount'])))
        }
    }

    /**
     * Return the payment lines controls based on the given index.
     * @param index Index of the payment whose lines are needed.
     * @returns FormArray controls for the payment lines at the given index.
     */
    protected lineControls = (index) => {
        if (!this.payments || (this.payments.length < index)) {
            return []
        }

        return (this.payments.at(index).get('lines') as FormArray).controls
    }

    /**
     * 
     * @param due 
     * @returns 
     */
    protected dueToClass = (due: string): string => {
        return ago(due)
    }

    /**
     * Toggle the true/false value of the given payment selection
     * @param index Index of the payment whose selector button was checked.
     */
    protected toggleSelectedPay = (index: number) => {
        if ((index < 0) || (index > (this.payments.controls.length-1))) {
            return
        }

        const payment = this.payments.controls[index]
        if (!payment) {
            return
        }

        const selected = !payment.get('selected').value

        const lines = (payment.get('lines') as FormArray)
        if (!lines || (lines.controls.length <= 0)) {
            payment.get('amount').setValue(formatCurrency(0))
            payment.get('selected').setValue(false)
            this.computePayAmount(null)
            return
        }

        let total = 0
        for (const line of lines.controls) {
            const amount = clean(line.get('amount').value)
            line.get('selected').setValue(selected)

            if (!selected) {
                continue
            }

            if (amount > 0) {
                total += amount
                continue
            }

            const fullPayAmt = clean(line.get('full').value)
            line.get('amount').setValue(formatCurrency(fullPayAmt))
            total += fullPayAmt
        }

        payment.get('amount').setValue(formatCurrency(total))
        payment.get('selected').setValue(selected)
        this.computePayAmount(null)
    }

    /**
     * Toggle the true/false value of the given payment selection
     * @param index Index of the payment whose selector button was checked.
     */
    protected toggleSelectedLine = (index: number, jndex: number) => {
        if ((index < 0) || (index > (this.payments.controls.length-1))) {
            return
        }

        const payment = this.payments.controls[index]
        if (!payment) {
            return
        }

        const lines = (payment.get('lines') as FormArray)

        if ((!lines) || (jndex < 0) || (jndex > lines.controls.length)) {
            return
        }

        const selectedLine = lines.controls[jndex]

        const amount = clean(selectedLine.get('amount').value)

        const selected = !selectedLine.get('selected').value
        if (selected && (amount <= 0)) {
            const fullPayAmt = clean(selectedLine.get('full').value)
            selectedLine.get('amount').setValue(formatCurrency(fullPayAmt))
        }

        selectedLine.get('selected').setValue(selected)

        let total = 0
        let atLeastOneSelected = false
        for (const line of lines.controls) {
            if (!line.get('selected').value) {
                continue
            }

            atLeastOneSelected = true

            const amount = clean(line.get('amount').value)
            if (amount > 0) {
                total += amount
                continue
            }

            const fullPayAmt = clean(line.get('full').value)
            line.get('amount').setValue(formatCurrency(fullPayAmt))
            total += fullPayAmt
        }

        payment.get('amount').setValue(formatCurrency(total))
        payment.get('selected').setValue(atLeastOneSelected)
        this.computePayAmount(null)
    }

    /**
     * Handle changes to a line item amount
     * @param {Event} e The event that triggered this computation, if any..
     * @param index Index of the parent payment that changed
     * @param jndex Index of the payment line item that changed.
     */
    protected lineItemChanged = (e: Event, index: number, jndex: number) => {
        if ((index < 0) || (index > (this.payments.controls.length-1))) {
            return
        }

        const payment = this.payments.controls[index]
        if (!payment) {
            return
        }

        const lines = (payment.get('lines') as FormArray)
        if ((!lines) || (jndex < 0) || (jndex > (lines.controls.length-1))) {
            return
        }

        let total = 0
        let atLeastOneSelected = false
        for (const line of lines.controls) {
            if (!line.get('selected').value) {
                continue
            }

            const amount = clean(line.get('amount').value)
            if (amount <= 0) {
                line.get('selected').setValue(false)
                continue
            }

            atLeastOneSelected = true
            total += amount
        }

        payment.get('amount').setValue(formatCurrency(total))
        payment.get('selected').setValue(atLeastOneSelected)

        const changedLine = lines.controls[jndex]
        if (changedLine.pristine) { // number didnt change, prolly just a blur event that triggered this.
            return
        }

        changedLine.get('selected').setValue(true)
        changedLine.markAsPristine()

        /** If user changed the amount to pay (dirty) then we don't want to recompute the pay amount */
        if (this.form.get('amount').dirty) {
            this.selectPays(null, {index, jndex})
            return
        }
        
        this.computePayAmount(null)
    }

    /**
     * After a payment selection changes or an amount changes 
     * we need to recompute the total payment amount (amount 
     * to pay).
     * @param {Event} e The event that triggered this computation, if any.
     */
    protected computePayAmount = (e: Event) => {
        if (!this.payments || (this.payments.controls.length <= 0)) {
            this.form.get('amount').setValue(formatCurrency(0), {emitEvent: false})
            return
        }

        let amount = 0
        for (let payment of this.payments.controls) {
            if (!payment.get('selected').value) {
                continue
            }

            const payAmt = clean(payment.get('amount').value)
            if (payAmt <= 0) {
                payment.get('amount').setValue(formatCurrency(0), {emitEvent: false})
                payment.get('selected').setValue(false, {emitEvent: false})
                continue
            }

            amount += payAmt
        }

        this.form.get('amount').setValue(formatCurrency(amount), {emitEvent: false})
        this.form.get('amount').markAsPristine()

        this.account.get('paidIn100s').setValue(formatCurrency(amount - (amount % 100)))
    }

    /**
     * If the total payment amount changes we need to toggle the 
     * selections of the payments that could be covered by the 
     * new amount.
     * @param {Event} e
     */
    protected selectPays = (e: Event, fixed: PayLine = null) => {
        if (!this.payments || (this.payments.controls.length <= 0)) {
            return
        }

        let amount = clean(this.form.get('amount').value)
        this.account.get('paidIn100s').setValue(formatCurrency(amount - (amount % 100)))

        const sortables = []
        for (let index=0; index < this.payments.controls.length; index++) {
            const payment = this.payments.controls[index]
            payment.get('selected').setValue(false)
            payment.get('amount').setValue(formatCurrency(0))

            const lines = (payment.get('lines') as FormArray)
            if ((!lines) || (lines.controls.length <= 0)) {
                continue
            }

            for (let jndex=0; jndex < lines.controls.length; jndex++) {
                const line = lines.controls[jndex]
                const fullPayAmt = clean(line.get('full').value)
                sortables.push({
                    index: index,
                    jndex: jndex,
                    due: moment(line.get('due').value).milliseconds,
                    fixed: (fixed && (index == fixed.index) && (jndex == fixed.jndex)),
                    fixedAmount: clean(line.get('amount').value),
                    amount: fullPayAmt,
                    category: line.get('category').value.toLowerCase()
                })
            }
        }

        sortables.sort((a, b) => a.due - b.due)
        if (this.allocate) {
            sortables.sort((a, b) => this.allocate.indexOf(a.category) - this.allocate.indexOf(b.category))
        }

        const mvLoan: PayLine = { index: -1, jndex: -1 }

        for (const item of sortables) {
            const payment = this.payments.controls[item.index]
            const line = (payment.get('lines') as FormArray).controls[item.jndex]
            const category = line.get('category').value

            if (((mvLoan.index < 0) || (mvLoan.jndex < 0)) && (category.toLowerCase() == 'mv loan')) {
                mvLoan.index = item.index
                mvLoan.jndex = item.jndex
            }

            if (amount <= 0) {
                line.get('amount').setValue(formatCurrency(0), {emitEvent: false})
                continue
            }

            if (item.fixed) {
                amount -= item.fixedAmount
                continue
            }

            const fullPayAmt = item.amount
            
            if (amount < fullPayAmt) {
                line.get('amount').setValue(formatCurrency(amount), {emitEvent: false})
                line.get('selected').setValue(true)
                this.lineItemChanged(undefined, item.index, item.jndex)
                amount = 0
                continue
            }

            line.get('amount').setValue(formatCurrency(fullPayAmt), {emitEvent: false})
            line.get('selected').setValue(true)
            this.lineItemChanged(undefined, item.index, item.jndex)
            amount -= fullPayAmt
        }

        if (amount <= 0) { // fully distrubuted?
            return
        }

        if (!mvLoan || (mvLoan.index < 0) || (mvLoan.jndex < 0)) {
            return
        }

        const payment = this.payments.controls[mvLoan.index]
        const line = (payment.get('lines') as FormArray).controls[mvLoan.jndex]

        const current = clean(line.get('amount').value)

        line.get('amount').setValue(formatCurrency(current + amount), {emitEvent: false})
        line.get('selected').setValue(true)

        if (fixed) {
            return
        }

        this.lineItemChanged(undefined, mvLoan.index, mvLoan.jndex)
    }

    /**
     * 
     * @param address
     */
    protected zipSelected = (address: Object): void => {
        if (!address || !this.account) {
            return
        }

        this.account.get('city').setValue(address['city'])
        this.account.get('state').setValue(address['state'])
        this.account.get('zipCode').setValue(address['zipcode'])

        this.zipCodeList = []
    }

    /**
     * Keep track of what terms were selected when 
     * the user clicked the manage fees button.
     * @param termsId Unique internal identity of the terms that were selected by the user.
     */
    protected selectTerm = (termsId: number): void => {
        if (!termsId || (termsId <= 0)) {
            return
        }

        this.selectedTermsId = termsId
    }

    /**
     * Attempt to process the payment with the current payment and method details.
     * @param {Event} e Associated event for the take payment action.
     * @param {Boolean} [withStoredAcct = false] Indicates the customer is paying with a stored account rather than a new one.
     */
    protected take = (e: Event, withStoredAcct: Boolean = false) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        if (!this.payments || (this.payments.controls.length <= 0)) {
            return
        }

        if (this.form.invalid || this.account.invalid) {
            return
        }

        const payload = {
            paid: this.form.get('paid').value,
            amount: clean(this.form.get('amount').value),
            source: this.account.get('source').value,
            payments: []
        }

        let paidIn100s = clean(this.account.get('paidIn100s').value)
        const type = this.account.get('type').value.toLowerCase()

        for (const payment of this.payments.controls) {
            if (!payment.get('selected').value) {
                continue
            }

            const lines = (payment.get('lines') as FormArray)
            if (!lines || (lines.controls.length <= 0)) {
                continue
            }

            for (const line of lines.controls) {
                if (!line.get('selected').value) {
                    continue
                }

                const amount = clean(line.get('amount').value)
                if (!amount || isNaN(amount) || (amount <= 0)) {
                    continue
                }

                const meta = {
                    saleId: parseInt(payment.get('id').value),
                    category: line.get('category').value,
                    amount: amount,
                    type: type
                }

                const accountId = parseInt(this.form.get('accountId').value)
                if (withStoredAcct && accountId && !isNaN(accountId) && (accountId > 0)) {
                    payload.source = this.form.get('source').value
                    meta['accountId'] = accountId
                    payload.payments.push(meta)
                    continue
                }

                switch (type) {
                    case 'cash':
                        if (paidIn100s <= 0) {
                            break
                        }

                        const maxPaidIn100s = (Math.trunc(amount / 100) * 100)
                        if (paidIn100s <= maxPaidIn100s) {
                            meta['paidIn100s'] = paidIn100s
                            paidIn100s = 0
                            break
                        }

                        meta['paidIn100s'] = maxPaidIn100s
                        paidIn100s -= maxPaidIn100s
                        break
                    case 'check':
                    case 'money order':
                    case 'cashiers check':
                    case 'travelers check':
                        meta['checkNumber'] = this.account.get('checkNumber').value
                        break
                    case 'credit card':
                    case 'debit card':
                        const year = parseInt(this.account.get('expiry_year').value)
                        const month = parseInt(this.account.get('expiry_month').value)
                        const expiry = moment(`${year}-${month}-01`).endOf('month').format('YYYY-MM-DD')
                        
                        meta['holder'] = this.account.get('holder').value
                        meta['address'] = this.account.get('address').value
                        meta['city'] = this.account.get('city').value
                        meta['state'] = this.account.get('state').value
                        meta['zipCode'] = this.account.get('zipCode').value
                        meta['number'] = this.account.get('number').value
                        meta['code'] = this.account.get('code').value
                        meta['expires'] = expiry
                        meta['save'] = (this.account.get('save').value === true)
                        break
                    case 'ach checking':
                    case 'ach savings':
                    case 'bank draft':
                        meta['holder'] = this.account.get('holder').value
                        meta['address'] = this.account.get('address').value
                        meta['city'] = this.account.get('city').value
                        meta['state'] = this.account.get('state').value
                        meta['zipCode'] = this.account.get('zipCode').value
                        meta['route'] = this.account.get('route').value
                        meta['number'] = this.account.get('number').value
                        meta['code'] = this.account.get('code').value
                        meta['save'] = (this.account.get('save').value === true)
                        break
                    case 'third-party':
                        meta['confirmation'] = this.account.get('confirmation').value
                        break
                }

                payload.payments.push(meta)
            }
        }

        if (!payload || !payload.payments || (payload.payments.length <= 0)) {
            return
        }

        if ((type === 'cash') && (paidIn100s > 0)) { // still have some unallocated 100s?
            payload.payments[0]['paidIn100s'] += paidIn100s
        }

        this.master.post(`payments/pay`, payload, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.paymentTaken.next({amount: payload.amount})
            this.fetchPaymentAccounts()
            this.reset()
        })
    }

    /**
     * Listen for fee change events from the payment fees component then update 
     * the display and pass the changes to other components.
     * @param meta The fee change details.
     */
    protected feesChanged = (meta: Object) => {
        this.paymentTaken.next({amount: 0})
    }

    /**
     * Determine primary address from a list of payer addresses and set the 
     * account address meta.
     * @param addresses
     */
    private determineAddress = (addresses) => {
        if (!addresses || (addresses.length <= 0)) {
            return
        }

        const primary = addresses.find((addy) => addy.primaryAddress)
        if (!primary) {
            return
        }
        
        this.account.get('address').setValue(primary.address, {emitEvent: false})
        this.account.get('city').setValue(primary.city, {emitEvent: false})
        this.account.get('state').setValue(primary.state, {emitEvent: false})
        this.account.get('zipCode').setValue(primary.zipCode, {emitEvent: false})
    }
}
